import React, { useState, useEffect } from 'react';

const AttendanceSummary = ({ onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTime, setRefreshTime] = useState(null);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch('/api/gla/attendance-summary', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response (possibly proxy error)');
      }
      
      const result = await response.json();
      
      if (result.success) {
        setData(result.data);
        setRefreshTime(new Date().toLocaleString());
      } else {
        setError(result.message || 'Failed to fetch summary');
      }
    } catch (err) {
      let errorMsg = err.message;
      if (err.name === 'AbortError') {
        errorMsg = 'Request timeout - backend server not responding';
      }
      setError(`Unable to load attendance summary: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchSummary();
  }, []);

  const downloadCSV = () => {
    if (data.length === 0) {
      alert('No data to download');
      return;
    }

    const headers = ['GL Number', 'Group', 'Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7', 'Week 8', 'Week 9', 'Total Points', 'Attended Weeks', 'Percentage'];
    const csvContent = [
      headers.join(','),
      ...data.map(row => [
        row.glNumber,
        row.email,
        row.week1,
        row.week2,
        row.week3,
        row.week4,
        row.week5,
        row.week6,
        row.week7,
        row.week8,
        row.week9,
        row.totalPoints,
        row.attendedWeeks,
        row.percentage
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Attendance-Summary-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        maxWidth: '1400px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '2rem'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', borderBottom: '2px solid #e8e8e8', paddingBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: 700, color: '#1a1a1a' }}>
              Attendance Summary (10 Points per Week)
            </h2>
            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
              {refreshTime && `Last updated: ${refreshTime}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#e8e8e8',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 600,
              transition: 'all 300ms ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#d8d8d8'}
            onMouseOut={(e) => e.target.style.background = '#e8e8e8'}
          >
            ✕ Close
          </button>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          <button
            onClick={fetchSummary}
            disabled={loading}
            style={{
              background: '#7A5107',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 600,
              transition: 'all 300ms ease',
              opacity: loading ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!loading) e.target.style.background = '#5a3a05';
            }}
            onMouseOut={(e) => {
              if (!loading) e.target.style.background = '#7A5107';
            }}
          >
            <i className="fas fa-refresh" style={{ marginRight: '0.5rem' }}></i>
            {loading ? 'Loading...' : 'Generate Report'}
          </button>

          <button
            onClick={downloadCSV}
            disabled={data.length === 0}
            style={{
              background: '#1a1a1a',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: data.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 600,
              transition: 'all 300ms ease',
              opacity: data.length === 0 ? 0.5 : 1
            }}
            onMouseOver={(e) => {
              if (data.length > 0) e.target.style.background = '#2d2d2d';
            }}
            onMouseOut={(e) => {
              if (data.length > 0) e.target.style.background = '#1a1a1a';
            }}
          >
            <i className="fas fa-download" style={{ marginRight: '0.5rem' }}></i>
            Download CSV
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: '6px',
            padding: '1rem',
            marginBottom: '2rem',
            color: '#c62828',
            fontSize: '0.95rem'
          }}>
            <i className="fas fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
            {error}
          </div>
        )}

        {/* Table */}
        {data.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem'
            }}>
              <thead>
                <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e8e8e8' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>GL Number</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 700, color: '#1a1a1a' }}>Group</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W1</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W2</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W3</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W4</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W5</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W6</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W7</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W8</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a' }}>W9</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a', background: '#fff9e6' }}>Total Points</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a', background: '#fff9e6' }}>Weeks</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#1a1a1a', background: '#fff9e6' }}>%</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e8e8e8', background: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }}>
                    <td style={{ padding: '1rem', color: '#1a1a1a', fontWeight: 600 }}>{row.glNumber}</td>
                    <td style={{ padding: '1rem', color: '#666' }}>{row.email}</td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week1 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week1 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week2 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week2 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week3 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week3 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week4 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week4 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week5 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week5 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week6 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week6 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week7 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week7 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week8 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week8 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: row.week9 === 10 ? '#2e7d32' : '#999' }}>
                      {row.week9 === 10 ? '✓' : '-'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#7A5107', background: '#fff9e6' }}>{row.totalPoints}</td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#7A5107', background: '#fff9e6' }}>{row.attendedWeeks}</td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: '#7A5107', background: '#fff9e6' }}>{row.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.length === 0 && !loading && !error && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            <p>Click "Generate Report" to load attendance summary data</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceSummary;
